import { api } from './api/client';

/**
 * Form Configuration Service
 * Handles dynamic form field configuration and dropdown options
 */

export interface FieldConfiguration {
  id: number;
  field_name: string;
  is_enabled: boolean;
  is_required: boolean;
  is_system: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface ApplicationType {
  id: number;
  value: string;
  label_en: string;
  label_ar: string;
  badge_color: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApplicationPriority {
  id: number;
  value: string;
  label_en: string;
  label_ar: string;
  badge_color: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApplicationStatus {
  id: number;
  value: string;
  label_en: string;
  label_ar: string;
  badge_color: string;
  display_order: number;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

// Field Configuration API
export const getFieldConfigurations = async (): Promise<FieldConfiguration[]> => {
  const response = await api.get('/transactions/form-config/fields/');
  // Handle both paginated and non-paginated responses
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const updateFieldConfiguration = async (
  id: number,
  data: Partial<FieldConfiguration>
): Promise<FieldConfiguration> => {
  const response = await api.patch(`/transactions/form-config/fields/${id}/`, data);
  return response.data;
};

// Application Types API
export const getApplicationTypes = async (): Promise<ApplicationType[]> => {
  const response = await api.get('/transactions/form-config/application-types/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const getActiveApplicationTypes = async (): Promise<ApplicationType[]> => {
  const response = await api.get('/transactions/form-config/application-types/active/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const createApplicationType = async (
  data: Omit<ApplicationType, 'id' | 'created_at' | 'updated_at'>
): Promise<ApplicationType> => {
  const response = await api.post('/transactions/form-config/application-types/', data);
  return response.data;
};

export const updateApplicationType = async (
  id: number,
  data: Partial<ApplicationType>
): Promise<ApplicationType> => {
  const response = await api.patch(`/transactions/form-config/application-types/${id}/`, data);
  return response.data;
};

export const deleteApplicationType = async (id: number): Promise<void> => {
  await api.delete(`/transactions/form-config/application-types/${id}/`);
};

// Priorities API
export const getPriorities = async (): Promise<ApplicationPriority[]> => {
  const response = await api.get('/transactions/form-config/priorities/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const getActivePriorities = async (): Promise<ApplicationPriority[]> => {
  const response = await api.get('/transactions/form-config/priorities/active/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const createPriority = async (
  data: Omit<ApplicationPriority, 'id' | 'created_at' | 'updated_at'>
): Promise<ApplicationPriority> => {
  const response = await api.post('/transactions/form-config/priorities/', data);
  return response.data;
};

export const updatePriority = async (
  id: number,
  data: Partial<ApplicationPriority>
): Promise<ApplicationPriority> => {
  const response = await api.patch(`/transactions/form-config/priorities/${id}/`, data);
  return response.data;
};

export const deletePriority = async (id: number): Promise<void> => {
  await api.delete(`/transactions/form-config/priorities/${id}/`);
};

// Statuses API
export const getStatuses = async (): Promise<ApplicationStatus[]> => {
  const response = await api.get('/transactions/form-config/statuses/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const getActiveStatuses = async (): Promise<ApplicationStatus[]> => {
  const response = await api.get('/transactions/form-config/statuses/active/');
  return Array.isArray(response.data) ? response.data : response.data.results || [];
};

export const createStatus = async (
  data: Omit<ApplicationStatus, 'id' | 'created_at' | 'updated_at' | 'is_system'>
): Promise<ApplicationStatus> => {
  const response = await api.post('/transactions/form-config/statuses/', data);
  return response.data;
};

export const updateStatus = async (
  id: number,
  data: Partial<ApplicationStatus>
): Promise<ApplicationStatus> => {
  const response = await api.patch(`/transactions/form-config/statuses/${id}/`, data);
  return response.data;
};

export const deleteStatus = async (id: number): Promise<void> => {
  await api.delete(`/transactions/form-config/statuses/${id}/`);
};
